import { useState } from 'react';
import { Link } from 'react-router-dom';
import { featuredServices, additionalServices } from '@/mocks/services';

export default function ServicesOverview() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section className="py-16 md:py-24 px-4 md:px-6 lg:px-10 bg-background-100 relative overflow-hidden">
      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block text-xs md:text-sm font-medium tracking-wider uppercase text-accent-600 mb-3">
            What We Do
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            Our <span className="text-primary-500">Services</span>
          </h2>
          <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            From complete home transformations to essential repairs, our skilled team handles projects of every size with the same dedication to quality and customer satisfaction.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-14">
          {featuredServices.map((service, idx) => (
            <Link
              key={service.slug}
              to={`/services/${service.slug}`}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="group relative rounded-xl overflow-hidden cursor-pointer h-[340px] md:h-[380px]"
            >
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/5 transition-opacity duration-500 group-hover:from-black/85 group-hover:via-black/40"></div>

              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/90 text-background-50 mb-3 transition-transform duration-300 group-hover:scale-110">
                  <i className={`${service.icon} text-base`}></i>
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-background-50 mb-1">
                  {service.title}
                </h3>
                <p className={`text-sm text-background-50/80 leading-relaxed transition-all duration-300 ${
                  hoveredIndex === idx ? 'opacity-100 translate-y-0 max-h-20' : 'opacity-0 translate-y-2 max-h-0'
                }`}>
                  {service.shortDesc}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
            More <span className="text-accent-500">Services</span> We Offer
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {additionalServices.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="group flex items-center gap-3 p-3.5 md:p-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200/60 hover:bg-primary-50/40 transition-all cursor-pointer"
              >
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${service.icon} text-sm`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {service.title}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
          >
            View All Services
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}